import { DateTime } from "luxon";
import {
  GridColDef,
  gridDateComparator,
  gridNumberComparator,
  gridStringOrNumberComparator,
} from "@mui/x-data-grid-pro";

import { headerType } from "../types";
import { getRenderComponent } from "./getRenderComponent";

export const dynamicHeaderGenerator = (
  headerList: headerType[],
  rows: any[],
  isOpportunity = true,
  onCellClick,
  viewWidth,
  renderCellMapping = [{
    field: 'DEFAULT',
    renderCell: 'TEXT',
  }],
  visibleColumns,
  filterableFields: string[] = [],
): GridColDef[] | undefined => {
  try {
    let generatedHeaderLst: any = [];
    let headers = JSON.parse(JSON.stringify(headerList));
    headers = isOpportunity ? sortHeaders(headers) : headers;

    headers.forEach((header: headerType) => {
      const fieldName = isOpportunity ? header.db_field : header.input_file_field;
      
      const column: GridColDef = {
        field: fieldName,
        headerName: header.display_name,
        align: "left",
        headerAlign: "left",
        renderHeader: () => <strong>{header.display_name}</strong>,
        filterable: filterableFields.includes(header.db_field),
        
        // Add filter operators for client-side filtering
        filterOperators: (() => {
          switch (header.field_data_type) {
            case "date":
              return [
                {
                  value: 'is',
                  label: 'Is',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return new Date(params.value).getTime() === new Date(filterItem.value).getTime();
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'not',
                  label: 'Is not',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return new Date(params.value).getTime() !== new Date(filterItem.value).getTime();
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'before',
                  label: 'Before',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return new Date(params.value) < new Date(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'after',
                  label: 'After',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return new Date(params.value) > new Date(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
              ];
            
            case "number":
            case "number_percent":
              return [
                {
                  value: '=',
                  label: 'Equals',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) === Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: '!=',
                  label: 'Not equals',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) !== Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: '>',
                  label: 'Greater than',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) > Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: '>=',
                  label: 'Greater than or equal',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) >= Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: '<',
                  label: 'Less than',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) < Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: '<=',
                  label: 'Less than or equal',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return Number(params.value) <= Number(filterItem.value);
                    };
                  },
                  InputComponent: undefined,
                },
              ];
            
            default: // string, text, etc.
              return [
                {
                  value: 'contains',
                  label: 'Contains',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return String(params.value).toLowerCase().includes(String(filterItem.value).toLowerCase());
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'equals',
                  label: 'Equals',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return String(params.value).toLowerCase() === String(filterItem.value).toLowerCase();
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'startsWith',
                  label: 'Starts with',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return String(params.value).toLowerCase().startsWith(String(filterItem.value).toLowerCase());
                    };
                  },
                  InputComponent: undefined,
                },
                {
                  value: 'endsWith',
                  label: 'Ends with',
                  getApplyFilterFn: (filterItem) => {
                    return (params) => {
                      if (!filterItem.value) return true;
                      return String(params.value).toLowerCase().endsWith(String(filterItem.value).toLowerCase());
                    };
                  },
                  InputComponent: undefined,
                },
              ];
          }
        })(),

        renderCell: (params) => {
          try {
            if (!params || !params.row) return null;
        
            // Safe field name
            const safeFieldName = fieldName ?? "";
        
            // Try to find a matching renderCell type
            const matchedMapping = Array.isArray(renderCellMapping)
              ? renderCellMapping.find((field) => field?.field === safeFieldName)
              : null;
        
            const renderCellType = matchedMapping?.renderCell ?? "DEFAULT";
            const RenderComponent = getRenderComponent(renderCellType);
        
            // If component found, render it
            if (RenderComponent && typeof RenderComponent === "function") {
              return (
                <RenderComponent
                  params={params}
                  onClick={onCellClick}
                >
                  {params.value}
                </RenderComponent>
              );
            }
        
            // fallback if no custom component
            return params.value ?? null;
          } catch (err) {
            console.error("Error in renderCell:", err);
            return null; // fail silently
          }
        },
      };

      switch (header.field_data_type) {
        case "date":
          column.sortComparator = gridDateComparator;
          column.valueParser = (value: string) => value || "";
          column.valueFormatter = (params) =>
            (params?.value ? DateTime.fromISO(params.value).toFormat("dd LLL yyyy") : "");
          break;

        case "number":
          column.sortComparator = gridNumberComparator;
          column.valueParser = (value: string) => (value ? parseInt(value, 10) : "");
          column.valueFormatter = (params) => params?.value || "";
          break;

        case "number_percent":
          column.sortComparator = gridNumberComparator;
          column.valueParser = (value: string) => value || "";
          column.valueFormatter = (params) =>
            params?.value ? `${Math.round((params.value + Number.EPSILON) * 100)}%` : "";
          break;

        default:
          column.sortComparator = gridStringOrNumberComparator;
          column.valueParser = (value: string) => value || "";
          column.valueFormatter = (params) => params?.value || "";
          break;
      }

      generatedHeaderLst.push(column);
    });

    if(visibleColumns && visibleColumns.length) {
      // Filter and reorder columns based on visibleColumns order
      const orderedHeaders: GridColDef[] = [];
      visibleColumns.forEach(field => {
        const header = generatedHeaderLst.find(h => h.field === field);
        if (header) {
          orderedHeaders.push(header);
        }
      });
      generatedHeaderLst = orderedHeaders;
    }

    return calculateColumnWidths(rows, generatedHeaderLst, viewWidth);
  } catch (error) {
  }
};

const sortHeaders = (headersArray) => {
  try {
    // Fields that need sorting
    const sortableFields = [
      "lead_details_in_a_string",
      "name",
      "first_name",
      "last_name",
      'Email_mailshake',
      "email",
      "segment",
      "engagement_index",
      "total_messages_sent",
      "lead_created_date_crm",
      "latest_lead_active_date",
      "nurturing_status",
      "nurturing_start_date",
    ];

    // Separate sortable and unsortable items
    const sortableItems = headersArray.filter((item) =>
      sortableFields.includes(item.db_field)
    );
    let nonSortableItems = headersArray.filter(
      (item) => !sortableFields.includes(item.db_field)
    );

    // Sort the sortable items based on the order in `sortableFields`
    let sortedSortableItems = sortableItems.sort((a, b) => {
      return (
        sortableFields.indexOf(a.db_field) - sortableFields.indexOf(b.db_field)
      );
    });

    // Combine the sorted and unsorted arrays
    return [...sortedSortableItems, ...nonSortableItems];
  } catch (error) {
    return headersArray; // Return original array if there's an error
  }
};

export const calculateColumnWidths = (rows, headers: any[], viewWidth: number) => {
  const charWidthInPx = 9;
  const minColumnWidth = 60;
  const maxColumnWidth = 300;

  let calculatedHeaders = headers.map((header) => {
    const columnNameLength = header.headerName.length;
    const maxDataLength = Math.max(
      ...rows.map(
        (row: { [x: string]: any }) => String(row[header.field] || "").length
      )
    );
    const maxLength = Math.max(columnNameLength + 2, maxDataLength);

    return {
      ...header,
      width: Math.min(
        Math.max(maxLength * charWidthInPx, minColumnWidth),
        maxColumnWidth
      ),
    };
  });

  // Total width of all columns
  const totalColumnsWidth = calculatedHeaders.reduce((sum, col) => sum + col.width, 0);

  // If total width is less than view width, distribute width equally
  if (totalColumnsWidth < viewWidth) {

    calculatedHeaders = calculatedHeaders.map((col) => ({
      ...col,
      flex: 1,
    }));
  }

  return calculatedHeaders;
};
